import React, { useCallback, useState, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NumericFieldForm } from '@traquility/ui/diary';
import { IconPickerContainer } from './IconPickerContainer';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from '../ui/common/Dropdown';
import type {
  NumericFieldFormData,
  NumericFieldFormLabels,
  NumericFieldFormContainerProps,
  Category,
} from '../shared/types/numericFieldForm';

export type { NumericFieldFormContainerProps };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10,
    },
  },
});

const CREATE_NUMERIC_FIELD_MUTATION = `
  mutation CreateNumericField($input: CreateNumericFieldInput!) {
    createNumericField(input: $input) {
      numericField {
        id
        name
        icon
        score
      }
      errors
    }
  }
`;

const UPDATE_NUMERIC_FIELD_MUTATION = `
  mutation UpdateNumericField($input: UpdateNumericFieldInput!) {
    updateNumericField(input: $input) {
      numericField {
        id
        name
        icon
        score
      }
      errors
    }
  }
`;

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (convertActivities: boolean) => void;
  labels: NumericFieldFormLabels;
  isDeleting: boolean;
}

function DeleteModal({ isOpen, onClose, onDelete, labels: l, isDeleting }: DeleteModalProps) {
  const [convertActivities, setConvertActivities] = useState(true);

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{l.deleteTitle}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isDeleting}></button>
          </div>
          <div className="modal-body">
            <div className="form-check mb-2">
              <input
                type="radio"
                className="form-check-input"
                id="convert-true"
                checked={convertActivities}
                onChange={() => setConvertActivities(true)}
              />
              <label className="form-check-label" htmlFor="convert-true">
                {l.convertActivitiesLabel}
              </label>
              <div className="form-text">{l.convertActivitiesHelp}</div>
            </div>
            <div className="form-check">
              <input
                type="radio"
                className="form-check-input"
                id="convert-false"
                checked={!convertActivities}
                onChange={() => setConvertActivities(false)}
              />
              <label className="form-check-label text-danger" htmlFor="convert-false">
                {l.deleteActivitiesLabel}
              </label>
              <div className="form-text text-danger">{l.deleteActivitiesHelp}</div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isDeleting}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => onDelete(convertActivities)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.deleteButton}
                </>
              ) : (
                l.deleteButton
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ChangeCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (categoryId: string) => void;
  categories: Category[];
  currentCategoryId?: string;
  labels: NumericFieldFormLabels;
  isChanging: boolean;
}

function ChangeCategoryModal({
  isOpen,
  onClose,
  onConfirm,
  categories,
  currentCategoryId,
  labels: l,
  isChanging,
}: ChangeCategoryModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  useEffect(() => {
    if (isOpen) setSelectedCategoryId('');
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-folder me-2"></i>
              {l.changeCategoryTitle}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isChanging}></button>
          </div>
          <div className="modal-body">
            <div className="form-floating mb-3">
              <select
                className="form-select"
                id="new-category"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <option value="">— {l.selectCategory} —</option>
                {categories.map((cat) => (
                  <option
                    key={cat.id}
                    value={cat.id}
                    disabled={cat.id === currentCategoryId}
                    style={cat.id === currentCategoryId ? { fontStyle: 'italic', color: '#6c757d' } : {}}
                  >
                    {cat.name}{cat.id === currentCategoryId ? ` (${l.current})` : ''}
                  </option>
                ))}
              </select>
              <label htmlFor="new-category">{l.category}</label>
            </div>
            <p className="text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              {l.changeCategoryHelp}
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isChanging}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onConfirm(selectedCategoryId)}
              disabled={isChanging || !selectedCategoryId}
            >
              {isChanging ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.moveToCategory}
                </>
              ) : (
                l.moveToCategory
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NumericFieldFormContainerInner({
  graphqlEndpoint,
  csrfToken,
  initialData,
  backUrl,
  successUrl,
  labels,
  errors: initialErrors = {},
  mode,
  fieldTypes,
  unitOptions,
  categories = [],
}: NumericFieldFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [formIcon, setFormIcon] = useState(initialData.icon);
  const [formScore, setFormScore] = useState(initialData.score);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangeCategoryModal, setShowChangeCategoryModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingCategory, setIsChangingCategory] = useState(false);

  const handleSubmit = useCallback(async (data: NumericFieldFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const submitData = { ...data, icon: formIcon, score: formScore };
    const isEdit = mode === 'edit';
    const mutation = isEdit ? UPDATE_NUMERIC_FIELD_MUTATION : CREATE_NUMERIC_FIELD_MUTATION;

    const input: Record<string, unknown> = {
      name: submitData.name,
      icon: submitData.icon,
      score: submitData.score,
    };

    if (isEdit) {
      input.id = submitData.id;
    } else {
      input.categoryId = submitData.categoryId;
    }

    if (submitData.fieldType) input.fieldType = submitData.fieldType;
    if (submitData.minValue !== undefined) input.minValue = submitData.minValue;
    if (submitData.maxValue !== undefined) input.maxValue = submitData.maxValue;
    if (submitData.stepValue !== undefined) input.stepValue = submitData.stepValue;
    if (submitData.targetValue !== undefined) input.targetValue = submitData.targetValue;
    if (submitData.units) input.units = submitData.units;

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: mutation, variables: { input } }),
      });

      if (!response.ok) throw new Error('Network error');

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = isEdit ? result.data.updateNumericField : result.data.createNumericField;

      if (mutationResult.errors?.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      window.location.href = successUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, successUrl, mode, formIcon, formScore]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleIconSelect = useCallback((iconKey: string, score: number) => {
    setFormIcon(iconKey);
    setFormScore(score);
    setShowIconPicker(false);
  }, []);

  const handleScoreChange = useCallback((score: number) => {
    setFormScore(score);
  }, []);

  const handleDelete = useCallback(async (convertActivities: boolean) => {
    setIsDeleting(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation DeleteNumericField($input: DeleteNumericFieldInput!) {
              deleteNumericField(input: $input) {
                success
                errors
              }
            }
          `,
          variables: { input: { id: initialData.id, convertActivities } },
        }),
      });

      const result = await response.json();

      if (result.errors || result.data.deleteNumericField.errors?.length > 0) {
        const msgs = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.deleteNumericField.errors;
        setErrors({ base: msgs });
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsDeleting(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id, backUrl]);

  const handleChangeCategory = useCallback(async (newCategoryId: string) => {
    setIsChangingCategory(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation ChangeNumericFieldCategory($input: ChangeNumericFieldCategoryInput!) {
              changeNumericFieldCategory(input: $input) {
                numericField { id }
                errors
              }
            }
          `,
          variables: { input: { id: initialData.id, newCategoryId } },
        }),
      });

      const result = await response.json();

      if (result.errors || result.data.changeNumericFieldCategory.errors?.length > 0) {
        const msgs = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.changeNumericFieldCategory.errors;
        setErrors({ base: msgs });
        setIsChangingCategory(false);
        setShowChangeCategoryModal(false);
        return;
      }

      window.location.reload();
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsChangingCategory(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id]);

  const formDataWithIcon = {
    ...initialData,
    icon: formIcon,
    score: formScore,
  };

  return (
    <>
      {mode === 'edit' && (
        <div className="position-absolute" style={{ top: 10, right: 10 }}>
          <Dropdown>
            <DropdownTrigger className="btn btn-link text-secondary">
              <i className="bi bi-three-dots"></i>
            </DropdownTrigger>
            <DropdownMenu align="end">
              <DropdownItem onClick={() => setShowChangeCategoryModal(true)}>
                {labels.changeCategory}
              </DropdownItem>
              <DropdownDivider />
              <DropdownItem className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {labels.delete}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      <NumericFieldForm
        initialData={formDataWithIcon}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onIconPickerOpen={() => setShowIconPicker(true)}
        errors={errors}
        labels={labels}
        mode={mode}
        fieldTypes={fieldTypes}
        unitOptions={unitOptions}
        isSubmitting={isSubmitting}
      />

      {showIconPicker && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Icon</h5>
                <button type="button" className="btn-close" onClick={() => setShowIconPicker(false)}></button>
              </div>
              <div className="modal-body">
                <IconPickerContainer
                  graphqlEndpoint={graphqlEndpoint}
                  csrfToken={csrfToken}
                  initialIconKey={formIcon}
                  initialScore={formScore}
                  onSelect={handleIconSelect}
                  onScoreChange={handleScoreChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        labels={labels}
        isDeleting={isDeleting}
      />

      <ChangeCategoryModal
        isOpen={showChangeCategoryModal}
        onClose={() => setShowChangeCategoryModal(false)}
        onConfirm={handleChangeCategory}
        categories={categories}
        currentCategoryId={initialData.categoryId}
        labels={labels}
        isChanging={isChangingCategory}
      />
    </>
  );
}

function NumericFieldFormContainer(props: NumericFieldFormContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <NumericFieldFormContainerInner {...props} />
    </QueryClientProvider>
  );
}

let formRoot: Root | null = null;

export function mountNumericFieldForm(
  container: HTMLElement,
  props: NumericFieldFormContainerProps
): Root {
  if (formRoot) formRoot.unmount();
  const root = createRoot(container);
  formRoot = root;
  root.render(<NumericFieldFormContainer {...props} />);
  return root;
}

export function unmountNumericFieldForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default NumericFieldFormContainer;
